import { Col, Spinner2 } from 'components';
import { Api } from 'lib';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { IrreversableModal } from 'screens/Admin/components';
import Pagination from 'screens/Admin/screens/Moderation/components/Pagination';
import CampaignList from './CampaignList';
import CampaignPanel from './CampaignPanel';

const K = 'subscription.admin.campaigns';
const createButtonClass =
  'flex items-center gap-1.5 px-4 py-[7px] text-[13px] font-semibold text-white bg-[#20252D] rounded-md hover:bg-[#353E4A] active:scale-[0.98] transition-all';

const DEFAULT_PAGE_SIZE = 10;

// Active campaigns lead; the rest keep the server order (newest start first).
const activeFirst = (campaigns) => [
  ...campaigns.filter((c) => c.status === 'active'),
  ...campaigns.filter((c) => c.status !== 'active')
];

const listOf = (res) => (Array.isArray(res?.data) ? res.data : []);

const CampaignsTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [campaigns, setCampaigns] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [ending, setEnding] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const load = useCallback(async () => {
    setLoading(true);
    setCampaigns(listOf(await Api.get('/admin/featureCampaigns/list')));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    Api.get('/admin/featureCampaigns/features').then((res) => setFeatures(listOf(res)));
  }, [load]);

  const sorted = useMemo(() => activeFirst(campaigns), [campaigns]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visibleCampaigns = useMemo(
    () => sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sorted, currentPage, pageSize]
  );

  const openPanel = (campaign) => {
    setSelected(campaign);
    setPanelVisible(true);
  };

  async function confirmEnd() {
    const res = await Api.post(`/admin/featureCampaigns/end/${ending._id}`, {});
    setEnding(null);
    if (!res || res.err) return;
    dispatch({ type: 'HANDLE_RES', payload: { res: t(`${K}.success.ended`) } });
    load();
  }

  return (
    <>
      <div className="flex items-center justify-end px-6 py-3 border-b border-[#e5e7eb] bg-[#FAFBFC]">
        <button className={createButtonClass} onClick={() => openPanel(null)}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 1v12M1 7h12" strokeLinecap="round" />
          </svg>
          {t(`${K}.createButton`)}
        </button>
      </div>
        {loading && (
          <Col wid="100%" marg="80px 0" centerAll noFlex>
            <Spinner2 />
          </Col>
        )}
        {!loading && campaigns.length === 0 && (
          <div className="flex flex-col items-center text-center px-6 pt-20">
            <p className="text-[15px] font-semibold text-[#353E4A] mb-1.5">{t(`${K}.empty.title`)}</p>
            <p className="text-[13px] text-[#97A3B5]">{t(`${K}.empty.message`)}</p>
          </div>
        )}
        {!loading && campaigns.length > 0 && (
          <>
            <CampaignList campaigns={visibleCampaigns} onOpen={openPanel} onEnd={setEnding} />
            <div className="px-6 pb-6">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                total={sorted.length}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                onPrev={() => setPage(currentPage - 1)}
                onNext={() => setPage(currentPage + 1)}
                onGoToPage={setPage}
              />
            </div>
          </>
        )}

      <CampaignPanel
        visible={panelVisible}
        closeModal={() => setPanelVisible(false)}
        campaign={selected}
        features={features}
        onSaved={load}
      />
      <IrreversableModal
        visible={!!ending}
        heading={t(`${K}.endConfirmHeading`)}
        text={t(`${K}.endConfirmText`)}
        confirmText={t(`${K}.endNow`)}
        closeModal={() => setEnding(null)}
        onConfirm={confirmEnd}
      />
    </>
  );
};

export default CampaignsTab;
